import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Wine, Heart, Search, Filter, Lock, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { AdBanner } from '@/components/AdBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Cocktails() {
  const navigate = useNavigate();
  const { user, token, getAuthHeaders } = useAuth();
  const [cocktails, setCocktails] = useState([]);
  const [categories, setCategories] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const isPremium = user?.is_premium || false;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [cocktailsRes, categoriesRes, favoritesRes] = await Promise.all([
          axios.get(`${API}/cocktails`, { headers }),
          axios.get(`${API}/cocktails/categories/list`),
          token ? axios.get(`${API}/favorites`, { headers: getAuthHeaders() }) : Promise.resolve({ data: [] })
        ]);
        
        setCocktails(cocktailsRes.data);
        setCategories(categoriesRes.data.categories);
        setFavorites(new Set(favoritesRes.data.map(f => f.cocktail_id)));
      } catch (error) {
        console.error('Error fetching cocktails:', error);
        toast.error('Failed to load cocktails');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, getAuthHeaders]);

  const filteredCocktails = cocktails.filter(c => {
    const matchesSearch = search === '' || 
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.ingredients.some(i => i.toLowerCase().includes(search.toLowerCase()));
    const matchesCategory = selectedCategory === '' || selectedCategory === 'all' || c.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Split cocktails into free and premium
  const freeCocktails = filteredCocktails.filter(c => !c.is_premium);
  const premiumCocktails = filteredCocktails.filter(c => c.is_premium);

  const toggleFavorite = async (cocktailId, isPremiumCocktail) => {
    if (!isPremium) {
      toast.error('Favorites are a premium feature!', {
        action: {
          label: 'Upgrade',
          onClick: () => navigate('/pricing')
        }
      });
      return;
    }

    try {
      if (favorites.has(cocktailId)) {
        await axios.delete(`${API}/favorites/${cocktailId}`, { headers: getAuthHeaders() });
        setFavorites(prev => {
          const next = new Set(prev);
          next.delete(cocktailId);
          return next;
        });
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites`, { cocktail_id: cocktailId }, { headers: getAuthHeaders() });
        setFavorites(prev => new Set(prev).add(cocktailId));
        toast.success('Added to favorites!');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to update favorites';
      toast.error(msg);
    }
  };

  const handleCocktailClick = (cocktail) => {
    if (cocktail.is_premium && !isPremium) {
      toast.error('This is a premium cocktail!', {
        action: {
          label: 'Upgrade',
          onClick: () => navigate('/pricing')
        }
      });
      return;
    }
    navigate(`/cocktails/${cocktail.id}`);
  };

  const CocktailCard = ({ cocktail, index }) => {
    const isLocked = cocktail.is_premium && !isPremium;
    
    return (
      <div
        key={cocktail.id}
        className={`cocktail-card animate-fadeIn ${isLocked ? 'opacity-75' : ''}`}
        style={{ animationDelay: `${index * 0.05}s` }}
      >
        <div className="relative overflow-hidden">
          <img
            src={cocktail.image_url}
            alt={cocktail.name}
            className={`cocktail-card-image ${isLocked ? 'blur-[2px]' : ''}`}
          />
          
          {/* Premium Lock Overlay */}
          {isLocked && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <div className="text-center">
                <Lock className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
                <span className="text-xs text-[#D4AF37] font-bold">PREMIUM</span>
              </div>
            </div>
          )}
          
          {/* Favorite Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleFavorite(cocktail.id, cocktail.is_premium);
            }}
            className={`absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
              favorites.has(cocktail.id)
                ? 'bg-[#EF4444] text-white'
                : 'bg-black/40 text-white/70 hover:text-white'
            }`}
            data-testid={`favorite-btn-${cocktail.id}`}
          >
            <Heart className={`w-5 h-5 ${favorites.has(cocktail.id) ? 'fill-current' : ''}`} />
          </button>
          
          {/* Category Badge */}
          <div className="absolute bottom-3 left-3 flex gap-2">
            <span className="category-badge">{cocktail.category}</span>
            {cocktail.is_premium && (
              <span className="category-badge bg-[#D4AF37] text-black flex items-center gap-1">
                <Crown className="w-3 h-3" /> Premium
              </span>
            )}
          </div>
        </div>
        
        <div 
          className="p-5 cursor-pointer"
          onClick={() => handleCocktailClick(cocktail)}
        >
          <h3 className="text-lg font-bold text-[#F5F5F5] mb-2">{cocktail.name}</h3>
          <div className="flex flex-wrap gap-1">
            {cocktail.ingredients.slice(0, 3).map((ing, i) => (
              <span key={i} className="text-xs text-[#A3A3A3]">
                {ing}{i < Math.min(2, cocktail.ingredients.length - 1) ? ',' : ''}
              </span>
            ))}
            {cocktail.ingredients.length > 3 && (
              <span className="text-xs text-[#525252]">+{cocktail.ingredients.length - 3}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-32" data-testid="cocktails-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-[#F5F5F5] mb-3">
          Cocktail Library
        </h1>
        <p className="text-[#A3A3A3] text-lg">
          {isPremium ? (
            `Access all ${cocktails.length} cocktails`
          ) : (
            <>
              {freeCocktails.length} free cocktails • <span className="text-[#D4AF37]">{premiumCocktails.length} premium</span>
            </>
          )}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#525252]" />
          <Input
            type="text"
            placeholder="Search cocktails or ingredients..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-[#121212] border-white/10 focus:border-[#D4AF37] text-[#F5F5F5] placeholder:text-[#525252] rounded-xl"
            data-testid="search-input"
          />
        </div>
        
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger 
            className="w-[180px] bg-[#121212] border-white/10 text-[#A3A3A3] rounded-xl"
            data-testid="category-filter"
          >
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10">
            <SelectItem value="all" className="text-[#F5F5F5] focus:bg-[#D4AF37]/10 focus:text-[#D4AF37]">
              All Categories
            </SelectItem>
            {categories.map(cat => (
              <SelectItem
                key={cat}
                value={cat}
                className="text-[#F5F5F5] focus:bg-[#D4AF37]/10 focus:text-[#D4AF37] capitalize"
              >
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Results Count */}
      <p className="text-[#525252] text-sm mb-6">
        Showing {filteredCocktails.length} cocktail{filteredCocktails.length !== 1 ? 's' : ''}
      </p>

      {/* Cocktails Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={i} className="h-[320px] shimmer rounded-2xl" />
          ))}
        </div>
      ) : filteredCocktails.length === 0 ? (
        <div className="empty-state glass rounded-2xl py-16">
          <Wine className="w-12 h-12 text-[#525252] mb-4" />
          <h3 className="text-lg font-bold text-[#F5F5F5] mb-2">No cocktails found</h3>
          <p className="text-[#A3A3A3]">Try adjusting your search or filters</p>
        </div>
      ) : (
        <>
          {/* Free Cocktails */}
          {freeCocktails.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {freeCocktails.map((cocktail, index) => (
                <CocktailCard key={cocktail.id} cocktail={cocktail} index={index} />
              ))}
            </div>
          )}

          {/* Premium Cocktails Section */}
          {premiumCocktails.length > 0 && !isPremium && (
            <>
              <div className="flex items-center gap-4 my-8">
                <div className="h-px flex-1 bg-[#D4AF37]/30" />
                <div className="flex items-center gap-2 text-[#D4AF37]">
                  <Crown className="w-5 h-5" />
                  <span className="font-bold">Premium Cocktails</span>
                </div>
                <div className="h-px flex-1 bg-[#D4AF37]/30" />
              </div>
              
              <AdBanner variant="inline" />
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
                {premiumCocktails.map((cocktail, index) => (
                  <CocktailCard key={cocktail.id} cocktail={cocktail} index={index} />
                ))}
              </div>
            </>
          )}

          {/* Premium users see all cocktails mixed */}
          {isPremium && premiumCocktails.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {premiumCocktails.map((cocktail, index) => (
                <CocktailCard key={cocktail.id} cocktail={cocktail} index={freeCocktails.length + index} />
              ))}
            </div>
          )}
        </>
      )}
      
      {/* Ad Banner for free users */}
      <AdBanner />
    </div>
  );
}
